import { Tooltip } from "@ameelio/ui";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  useMediaQuery as measureScreenWidth,
} from "@mui/material";
import { SystemStyleObject, Theme } from "@mui/system";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import addStylesForDevices from "./addStylesForDevices";
import { belowLargeTablet } from "./responsiveHelpers";

type LinkButton = {
  label: string;
  to: string;
  state?: unknown;
  disabled?: boolean;
};
export type ClickButton = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

type Props = {
  id: string;
  variant?: "outlined" | "contained";
  buttons: (LinkButton | ClickButton)[];
  disabled?: boolean;
  tooltipTitle?: string;
  sx?: SystemStyleObject<Theme>;
};

function ButtonItem({
  sx,
  button,
}: {
  sx?: SystemStyleObject<Theme>;
  button: LinkButton | ClickButton;
}) {
  if ("to" in button) {
    return (
      <Button
        component={RouterLink}
        to={button.to}
        state={button.state}
        sx={sx}
        disabled={button.disabled}
      >
        {button.label}
      </Button>
    );
  }
  return (
    <Button onClick={button.onClick} sx={sx} disabled={button.disabled}>
      {button.label}
    </Button>
  );
}

export default function SplitButton({
  id,
  variant = "contained",
  buttons,
  disabled,
  tooltipTitle,
  sx,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMobileOrSmallTablet = belowLargeTablet(measureScreenWidth);

  const isOpen = !!anchorEl;
  const handleOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // when our buttons are rendered as links with an <a> tag, they
  // do not match other <button> tags for MUI's nth-of-type CSS
  // selectors. this may be fixed with newer versions of MUI. in
  // the meantime, we can specify the missing styles.
  // NOTE: these are specific to variant=outlined.
  const firstButtonSx: SystemStyleObject<Theme> =
    buttons.length > 1
      ? {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderRightColor: "transparent",
          "&:hover": {
            borderRightColor: "currentColor",
          },
          ...addStylesForDevices(isMobileOrSmallTablet, {}, { width: 1 }),
        }
      : addStylesForDevices(isMobileOrSmallTablet, {}, { width: 1 });
  const lastButtonSx: SystemStyleObject<Theme> = {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    marginLeft: "-1px",
  };
  // The tooltip is only intended to explain why the button group is disabled
  const title = disabled && tooltipTitle ? tooltipTitle : "";
  const buttonId = `${id}-button`;

  return (
    <ButtonGroup variant={variant} disableElevation disabled={disabled} sx={sx}>
      <Tooltip title={title}>
        <span style={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flex: 1,
            }}
          >
            <ButtonItem button={buttons[0]} sx={firstButtonSx} />
            {buttons.length > 1 && (
              <>
                <Button
                  size="small"
                  aria-controls={isOpen ? "split-button-menu" : undefined}
                  aria-expanded={isOpen ? "true" : undefined}
                  aria-haspopup="menu"
                  aria-label="more options"
                  onClick={isOpen ? handleClose : handleOpen}
                  sx={lastButtonSx}
                >
                  <ArrowDropDown />
                </Button>
                <Menu
                  slotProps={{ list: { "aria-labelledby": buttonId } }}
                  anchorEl={anchorEl}
                  open={isOpen}
                  onClick={handleClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  {buttons.slice(1).map((b) =>
                    "to" in b ? (
                      <MenuItem
                        key={b.label}
                        component={RouterLink}
                        to={b.to}
                        state={b.state}
                        disabled={b.disabled}
                      >
                        {b.label}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        key={b.label}
                        onClick={b.onClick}
                        disabled={b.disabled}
                      >
                        {b.label}
                      </MenuItem>
                    )
                  )}
                </Menu>
              </>
            )}
          </Box>
        </span>
      </Tooltip>
    </ButtonGroup>
  );
}
